/**
 * Pannello utenti: chi entra nel portale, da dove, e chi è amministratore.
 *
 * L'operazione per cui esiste è una sola — promuovere qualcuno ad
 * amministratore — ma è quella che dà accesso alle aree di **tutti** i clienti.
 * Per questo il comando chiede conferma invece di scattare al primo clic, e la
 * conferma nomina la persona: «rendere amministratore Mario Rossi?» si rilegge,
 * «sei sicuro?» no.
 *
 * I rifiuti (promuovere un cliente, togliersi l'amministrazione da soli) li
 * decide il server: qui i comandi sono disabilitati per non far provare, ma non
 * è lì che sta il controllo.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, admin } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { useDebounced } from '../hooks/useDebounced.js';
import type { PortalUser, PortalUsersResult } from '../lib/types.js';
import './UsersPage.css';

type Filtro = 'interni' | 'tutti' | 'admin';

const FILTRI: { id: Filtro; label: string; nota: string }[] = [
  {
    id: 'interni',
    label: 'Personale interno',
    nota: 'Gli unici che possono essere amministratori',
  },
  { id: 'admin', label: 'Amministratori', nota: 'Chi vede tutte le organizzazioni' },
  { id: 'tutti', label: 'Tutti gli utenti', nota: 'Compresi i clienti da AtlantePlus' },
];

const ORIGINE: Record<PortalUser['origine'], string> = {
  microsoft: 'Microsoft',
  atlanteplus: 'AtlantePlus',
  locale: 'Password locale',
  manuale: 'Inserita a mano',
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>('interni');
  const [q, setQ] = useState('');
  const [conferma, setConferma] = useState<{ user: PortalUser; admin: boolean } | null>(null);

  const ricerca = useDebounced(q.trim(), 300);

  const utenti = useQuery<PortalUsersResult>({
    queryKey: ['admin-users', filtro, ricerca],
    queryFn: () => admin.users(ricerca, filtro),
  });

  const modifica = useMutation({
    mutationFn: (v: { id: number; patch: { isAdmin?: boolean; isActive?: boolean } }) =>
      admin.updateUser(v.id, v.patch),
    onSuccess: () => {
      setConferma(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      // Se la modifica ha toccato chi sta guardando, cambia anche la sua sessione.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });

  const errore =
    modifica.error instanceof ApiError
      ? modifica.error.message
      : modifica.error
        ? 'Modifica non riuscita.'
        : null;

  return (
    <div className="users">
      <div className="users__head">
        <h1>Utenti</h1>
        <p className="users__lead">
          Chi accede al portale e con quale percorso. Un <strong>amministratore</strong> vede le
          aree di tutte le organizzazioni e ne può cambiare i permessi: per questo può esserlo solo
          il personale interno.
        </p>
      </div>

      <div className="users__filtri" role="group" aria-label="Filtro utenti">
        {FILTRI.map((f) => (
          <button
            key={f.id}
            type="button"
            title={f.nota}
            aria-pressed={filtro === f.id}
            className={filtro === f.id ? 'is-active' : undefined}
            onClick={() => setFiltro(f.id)}
          >
            {f.label}
          </button>
        ))}

        <input
          className="input users__cerca"
          type="search"
          value={q}
          placeholder="Cerca per nome, username o email…"
          aria-label="Cerca utenti"
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {errore ? (
        <div className="alert alert--error" role="alert">
          {errore}
        </div>
      ) : null}

      <div className="card users__panel">
        {utenti.isPending ? (
          <div className="users__state">Caricamento…</div>
        ) : utenti.isError ? (
          <div className="users__state">
            {utenti.error instanceof ApiError ? utenti.error.message : 'Elenco non disponibile.'}
          </div>
        ) : utenti.data.users.length === 0 ? (
          <div className="users__state">Nessuna utenza corrisponde.</div>
        ) : (
          <div className="users__scroll">
            <table className="users__table">
              <thead>
                <tr>
                  <th scope="col">Utenza</th>
                  <th scope="col">Origine</th>
                  <th scope="col">Ultimo accesso</th>
                  <th scope="col">Stato</th>
                  <th scope="col" className="users__num">
                    Ruolo
                  </th>
                </tr>
              </thead>
              <tbody>
                {utenti.data.users.map((u) => (
                  <tr key={u.id} className={u.isActive ? undefined : 'is-inactive'}>
                    <td>
                      <strong>{u.displayName ?? u.username}</strong>
                      <span className="users__code">
                        {u.username}
                        {u.seStesso ? ' — sei tu' : ''}
                      </span>
                    </td>
                    <td>
                      {ORIGINE[u.origine]}
                      {u.isInternal ? null : (
                        <span className="users__code">
                          cliente{u.atlantePrefix ? ` · ${u.atlantePrefix}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="users__muted">
                      {u.maiAccesso ? 'mai' : formatDate(u.lastLoginAt)}
                    </td>
                    <td>
                      {u.isActive ? (
                        <span className="users__badge users__badge--on">attiva</span>
                      ) : (
                        <span className="users__badge">disattivata</span>
                      )}
                    </td>
                    {/* Su un'utenza cliente il comando non c'è proprio: mostrarlo
                        disabilitato inviterebbe a chiedersi perché, e la risposta
                        — un admin vede tutti i clienti — non sta in un tooltip. */}
                    <td className="users__num">
                      {u.isInternal ? (
                        <button
                          type="button"
                          className={u.isAdmin ? 'btn btn--ghost btn--sm' : 'btn btn--sm'}
                          disabled={u.seStesso || modifica.isPending}
                          title={
                            u.seStesso
                              ? 'Non puoi cambiare il tuo stesso ruolo'
                              : u.isAdmin
                                ? 'Togli i privilegi di amministratore'
                                : 'Rendi amministratore'
                          }
                          onClick={() => setConferma({ user: u, admin: !u.isAdmin })}
                        >
                          {u.isAdmin ? 'Amministratore' : 'Promuovi'}
                        </button>
                      ) : (
                        <span className="users__muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {utenti.data ? (
        <p className="users__conteggio">
          {utenti.data.adminAttivi}{' '}
          {utenti.data.adminAttivi === 1 ? 'amministratore attivo' : 'amministratori attivi'}.
          {utenti.data.adminAttivi === 1
            ? ' Finché è uno solo non può essere declassato: promuovine un altro prima.'
            : ''}
        </p>
      ) : null}

      {conferma ? (
        <ConfermaRuolo
          nome={conferma.user.displayName ?? conferma.user.username}
          promuove={conferma.admin}
          inCorso={modifica.isPending}
          onAnnulla={() => {
            modifica.reset();
            setConferma(null);
          }}
          onConferma={() =>
            modifica.mutate({ id: conferma.user.id, patch: { isAdmin: conferma.admin } })
          }
        />
      ) : null}
    </div>
  );
}

function ConfermaRuolo({
  nome,
  promuove,
  inCorso,
  onAnnulla,
  onConferma,
}: {
  nome: string;
  promuove: boolean;
  inCorso: boolean;
  onAnnulla: () => void;
  onConferma: () => void;
}) {
  return (
    <div className="users__dialog" role="dialog" aria-modal="true" aria-label="Conferma ruolo">
      <div className="card users__dialog-box">
        <h3>
          {promuove ? `Rendere amministratore ${nome}?` : `Togliere l'amministrazione a ${nome}?`}
        </h3>
        <p>
          {promuove
            ? 'Vedrà le aree documentali di tutte le organizzazioni e ne potrà cambiare i permessi.'
            : 'Tornerà a vedere solo le organizzazioni a cui è associato.'}
        </p>
        <div className="users__dialog-actions">
          <button type="button" className="btn btn--ghost" onClick={onAnnulla} disabled={inCorso}>
            Annulla
          </button>
          <button type="button" className="btn" onClick={onConferma} disabled={inCorso}>
            {inCorso ? 'Attendere…' : promuove ? 'Rendi amministratore' : 'Togli'}
          </button>
        </div>
      </div>
    </div>
  );
}
